<template>
  <div class="vehicle-telematics" v-if="vehicle">

    <!-- Loading connection status -->
    <div v-if="loadingConnection" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <!-- Linked meter: a HEADER shown alongside the dashboard (a metered vehicle is both meter-linked AND
         connected — the two are not mutually exclusive). -->
    <div v-if="!loadingConnection && connection && connection.meter" class="d-flex align-center mb-4">
      <v-icon left small color="grey darken-1">mdi-counter</v-icon>
      <span class="text--secondary mr-1">{{ $t('vehicle.telematics-linked-meter') }} :</span>
      <router-link :to="{ name: 'meter-device.detail', params: { id: connection.meter.id } }">
        {{ connection.meter.name }}
      </router-link>
    </div>

    <!-- Connected state -->
    <template v-if="!loadingConnection && connection && connection.connected">
      <!-- Source switcher — only for a multi-connection vehicle (our test cars): pick which provider's last
           known state to display. Single-connection vehicles show no switcher. -->
      <div v-if="connections.length > 1" class="d-flex align-center flex-wrap mb-4" style="gap: 8px">
        <v-btn-toggle v-model="selectedConnectionIndex" dense mandatory class="cv-sources">
          <v-btn v-for="(c, i) in connections" :key="i" small :value="i">
            <v-icon left small>{{ getProviderIcon(c.provider) }}</v-icon>
            {{ c.provider }}
            <v-icon v-if="c.provider === chargeProvider" right small color="amber darken-2">mdi-star</v-icon>
          </v-btn>
        </v-btn-toggle>
        <!-- Le fournisseur dont les recharges font foi (CDR, remboursement) : un seul, même avec plusieurs connexions
             (véhicule d'essai). Choisi par l'opérateur, sinon celui du consentement actif. -->
        <template v-if="connection.meter">
          <span class="caption grey--text">
            <v-icon small color="amber darken-2">mdi-star</v-icon>
            {{ chargeProvider
              ? $t('vehicle.telematics-charge-provider', {provider: chargeProvider})
              : $t('vehicle.telematics-charge-provider-none') }}
            <span v-if="chargeProvider && !connection.meter.primaryProvider">{{ $t('vehicle.telematics-charge-provider-by-consent') }}</span>
          </span>
          <v-btn
            v-if="canChooseChargeProvider && activeConnection.provider !== chargeProvider"
            x-small outlined color="primary"
            :loading="busyAction === 'charge-provider'"
            :disabled="busyAction !== null && busyAction !== 'charge-provider'"
            @click="chooseChargeProvider(activeConnection.provider)"
          >{{ $t('vehicle.telematics-charge-provider-choose', {provider: activeConnection.provider}) }}</v-btn>
        </template>
      </div>

      <!-- Connection HEALTH (Lot 3): a lost/degraded connection is flagged here with the RIGHT message per mode
           (reconnect / re-authorise OEM / temporary OEM issue). The reconnect action itself lands in Lot 4. -->
      <v-alert v-if="healthBanner" :type="healthBanner.type" text prominent border="left" class="mb-4">
        <div class="font-weight-medium">
          <v-icon left :color="healthBanner.type">{{ healthBanner.icon }}</v-icon>{{ healthBanner.title }}
        </div>
        <div class="text-caption mt-1">
          {{ healthBanner.message }}
          <span v-if="healthBanner.since"> · {{ $t('vehicle.telematics-health.since', { date: formatDate(healthBanner.since, $t('format_datetime')) }) }}</span>
        </div>
        <div v-if="healthBanner.action" class="mt-2">
          <v-btn small :color="healthBanner.type" depressed :loading="busyAction === 'reconnect'" :disabled="isBusy && busyAction !== 'reconnect'" @click="reconnect" class="mr-2">
            <v-icon left small>mdi-car-connected</v-icon>{{ $t('vehicle.telematics-health.reconnect-action') }}
          </v-btn>
          <!-- Manual notification (Lot 5): email the driver the reconnect link instead of doing it here. -->
          <v-btn small text :color="healthBanner.type" :loading="busyAction === 'reconnect-email'" :disabled="isBusy && busyAction !== 'reconnect-email'" @click="sendReconnectLink">
            <v-icon left small>mdi-email-outline</v-icon>{{ $t('vehicle.telematics-health.reconnect-email') }}
          </v-btn>
        </div>
      </v-alert>

      <v-alert type="success" text dense class="mb-4">
        <div class="d-flex align-center justify-space-between">
          <div>
            <v-icon left color="success">mdi-car-connected</v-icon>
            {{ $t('vehicle.telematics-connected-via', { provider: activeConnection.provider || 'SmartCar' }) }}
            <span v-if="activeConnection.connectedAt" class="text--secondary ml-2">
              — {{ formatDate(activeConnection.connectedAt, $t('format_datetime')) }}
            </span>
            <!-- Provider-side onboarding status (Volteras async): en attente de validation / erreur / … -->
            <v-chip v-if="providerStatusChip" :color="providerStatusChip.color" text-color="white" x-small label class="ml-2">
              {{ providerStatusChip.label }}
            </v-chip>
            <!-- Traceability: the consent that opened this connection (granted date + when its email was sent). -->
            <div v-if="grantedConsent && grantedConsent.grantedAt" class="text-caption text--secondary mt-1">
              {{ $t('vehicle.telematics-consent-granted-on', { date: formatDate(grantedConsent.grantedAt, $t('format_datetime')) }) }}
              <span v-if="grantedConsent.version">· v{{ grantedConsent.version }}</span>
              <span v-if="grantedConsent.notifiedAt"> · {{ $t('vehicle.telematics-recap-consent-sent') }} {{ formatDate(grantedConsent.notifiedAt, $t('format_datetime')) }}</span>
            </div>
          </div>
          <div>
            <v-btn small text @click="refreshSignals" :loading="refreshing" class="mr-2">
              <v-icon left small>mdi-refresh</v-icon>
              {{ $t('refresh') }}
            </v-btn>
            <!-- Change the mode/type of an active connection (e.g. télématique simple → compteur): re-issues a
                 consent; the backend swaps the licence at grant. Works for VIN and OAuth alike. -->
            <v-btn small text color="primary" @click="showInitWizard = true" class="mr-2">
              <v-icon left small>mdi-swap-horizontal</v-icon>
              {{ $t('vehicle.telematics-change-connection') }}
            </v-btn>
            <!-- Multi-connection (gated ZEborne operator): add another provider, keeping this one. OAuth providers
                 open the consent link; VIN providers are declared directly. -->
            <v-menu v-if="connection.canMultiConnect" offset-y>
              <template #activator="{ on, attrs }">
                <v-btn small depressed rounded color="primary" v-on="on" v-bind="attrs" :loading="adding" class="mr-2">
                  <v-icon left small>mdi-plus</v-icon>
                  {{ $t('vehicle.telematics-add-connection') }}
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item v-for="p in (connection.multiConnectProviders || [])" :key="p.value" @click="pickMultiProvider(p)">
                  <v-list-item-icon class="mr-2"><v-icon small>{{ p.requiresDriverLink ? 'mdi-link-variant' : 'mdi-car-connected' }}</v-icon></v-list-item-icon>
                  <v-list-item-title>{{ p.label }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <!-- VIN/fleet providers (Link by Car): re-declare the VIN, plus disconnect to switch provider -->
            <template v-if="activeConnection.requiresDriverLink === false">
              <v-btn small text color="primary" @click="registerVehicle" :loading="registering" class="mr-2">
                <v-icon left small>mdi-cloud-upload-outline</v-icon>
                {{ $t('vehicle.telematics-declare') }}
              </v-btn>
              <v-btn small color="error" text @click="confirmTerminate">
                <v-icon left small>mdi-link-off</v-icon>
                {{ $t('vehicle.telematics-terminate') }}
              </v-btn>
            </template>
            <!-- Per-vehicle OAuth (SmartCar): disconnect -->
            <v-btn v-else small color="error" text @click="confirmTerminate">
              <v-icon left small>mdi-link-off</v-icon>
              {{ $t('vehicle.telematics-terminate') }}
            </v-btn>
          </div>
        </div>
      </v-alert>

      <!-- Identity strip: connection type + provider + VIN, for the selected source. -->
      <div class="cv-identity mb-4">
        <div class="cv-identity__item" v-if="activeConnectionTypeLabel">
          <span class="cv-identity__k">{{ $t('vehicle.telematics-recap-type') }}</span>
          <span class="cv-identity__v">{{ activeConnectionTypeLabel }}</span>
        </div>
        <div class="cv-identity__item">
          <span class="cv-identity__k">{{ $t('vehicle.telematics-provider') }}</span>
          <span class="cv-identity__v">{{ activeConnection.provider }}</span>
        </div>
        <div class="cv-identity__item" v-if="activeVin">
          <span class="cv-identity__k">VIN</span>
          <span class="cv-identity__v">{{ activeVin }}</span>
        </div>
      </div>

      <!-- A change of mode/type is awaiting the customer's consent: the vehicle stays on its current licence
           until the grant, so we flag the pending switch here. Only when it is a REAL change (isChange): a
           pending that merely re-consents an already-active provider in the same mode (e.g. an abandoned
           reconnect) is not a change and must not raise this banner. -->
      <v-alert v-if="pendingConsent && pendingConsent.isChange" type="info" text dense class="mb-4">
        <v-icon left small color="info">mdi-progress-clock</v-icon>
        {{ $t('vehicle.telematics-change-pending', { mode: pendingModeLabel, type: pendingTypeLabel }) }}
      </v-alert>

      <!-- No state yet for this source (connected but nothing collected/pushed) -->
      <div v-if="!snapshot" class="text-center py-8 text--secondary">
        <v-icon color="grey lighten-1" size="40" class="mb-2">mdi-cloud-question-outline</v-icon>
        <div>{{ $t('vehicle.telematics-no-state') }}</div>
      </div>

      <!-- Learned battery State-of-Health for THIS provider (measured from its own data), + degradation trend.
           The effective usable capacity here is what converts SoC→kWh; "source"/"confidence" say how it was got. -->
      <v-card v-if="activeConnection.soh" outlined class="mb-4">
        <v-card-text class="d-flex flex-wrap align-center" style="gap: 16px">
          <div>
            <div class="overline">{{ $t('vehicle.telematics-soh') }}</div>
            <div class="text-h5">
              {{ activeConnection.soh.sohPercent != null ? Math.round(activeConnection.soh.sohPercent) + '%' : '—' }}
              <span v-if="activeConnection.soh.effectiveCapacityKwh != null" class="text-caption text--secondary">
                · {{ formatNumber(activeConnection.soh.effectiveCapacityKwh, 1) }} kWh
              </span>
            </div>
          </div>
          <v-chip small :color="sohColor(activeConnection.soh.confidence)" dark>
            {{ $t('vehicle.telematics-soh-source.' + activeConnection.soh.source) }} ·
            {{ $t('vehicle.telematics-soh-confidence.' + activeConnection.soh.confidence) }}
          </v-chip>
          <v-chip v-if="activeConnection.soh.trend != null" small outlined :color="activeConnection.soh.trend >= 0 ? 'green' : 'orange'">
            <v-icon left small>{{ activeConnection.soh.trend >= 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}</v-icon>
            {{ activeConnection.soh.trend > 0 ? '+' : '' }}{{ activeConnection.soh.trend }}%
          </v-chip>
          <v-alert v-if="activeConnection.soh.degradation" type="warning" dense text class="mb-0 py-1">
            {{ $t('vehicle.telematics-soh-degradation') }}
          </v-alert>
          <v-spacer />
          <!-- SoH curve over the last year (fills in as the daily snapshot runs). -->
          <div v-if="sohSpark" class="text-right">
            <div class="caption text--secondary mb-1">{{ $t('vehicle.telematics-soh-trend') }}</div>
            <svg :width="sohSpark.w" :height="sohSpark.h" style="overflow: visible">
              <polyline :points="sohSpark.points" fill="none"
                        :stroke="activeConnection.soh.degradation ? '#FB8C00' : '#26A69A'"
                        stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />
              <circle :cx="sohSpark.last.x" :cy="sohSpark.last.y" r="2.5"
                      :fill="activeConnection.soh.degradation ? '#FB8C00' : '#26A69A'" />
            </svg>
          </div>
          <div v-else-if="activeConnection.soh.sohPercent != null" class="caption text--secondary">
            {{ $t('vehicle.telematics-soh-building') }}
          </div>
        </v-card-text>
      </v-card>

      <template v-if="snapshot">

        <!-- Last collected (from the event-sourced connection cache) -->
        <div v-if="activeConnection.lastCollectedAt" class="text-caption text--secondary mb-3 text-right">
          {{ $t('vehicle.telematics-last-collected') }} {{ timeAgo(activeConnection.lastCollectedAt) }}
        </div>

        <!-- Main dashboard -->
        <v-row dense>
          <!-- Battery gauge -->
          <v-col cols="12" sm="6" md="3" v-if="snapshot.batteryLevelPercent != null">
            <v-card outlined class="fill-height">
              <v-card-text class="text-center pa-4">
                <v-icon color="grey" class="mb-2">mdi-battery</v-icon>
                <div class="text-caption text--secondary mb-2">{{ $t('vehicle.telematics-battery-level') }}</div>
                <v-progress-circular
                    :value="snapshot.batteryLevelPercent"
                    :color="batteryColor"
                    :size="80"
                    :width="8"
                    rotate="-90"
                >
                  <span class="text-h6">{{ Math.round(snapshot.batteryLevelPercent) }}%</span>
                </v-progress-circular>
                <div v-if="snapshot.batteryRangeKm != null" class="text--secondary text-caption mt-2">
                  {{ $t('vehicle.telematics-range') }} : {{ Math.round(snapshot.batteryRangeKm) }} km
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Odometer -->
          <v-col cols="12" sm="6" md="3" v-if="snapshot.odometerKm != null">
            <v-card outlined class="fill-height">
              <v-card-text class="text-center pa-4">
                <v-icon color="grey" class="mb-2">mdi-counter</v-icon>
                <div class="text-caption text--secondary mb-2">{{ $t('vehicle.telematics-odometer') }}</div>
                <div class="text-h5">{{ Math.round(snapshot.odometerKm).toLocaleString() }}</div>
                <div class="text-caption text--secondary">km</div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Battery energy remaining (measured kWh in the pack — the reimbursement-relevant figure) -->
          <v-col cols="12" sm="6" md="3" v-if="snapshot.batteryEnergyKwh != null">
            <v-card outlined class="fill-height">
              <v-card-text class="text-center pa-4">
                <v-icon color="grey" class="mb-2">mdi-battery-charging-70</v-icon>
                <div class="text-caption text--secondary mb-2">{{ $t('vehicle.telematics-energy-remaining') }}</div>
                <div class="text-h5">{{ formatNumber(snapshot.batteryEnergyKwh, 1) }}</div>
                <div class="text-caption text--secondary">kWh</div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Charge status -->
          <v-col cols="12" sm="6" md="3" v-if="snapshot.chargingStatus != null || snapshot.isCharging != null">
            <v-card outlined class="fill-height">
              <v-card-text class="text-center pa-4">
                <v-icon color="grey" class="mb-2">mdi-ev-plug-type2</v-icon>
                <div class="text-caption text--secondary mb-2">{{ $t('vehicle.telematics-charge-status') }}</div>
                <v-chip small :color="chargeStateColor" text-color="white" class="mb-1">
                  {{ chargeStateLabel }}
                </v-chip>
                <div v-if="snapshot.isPluggedIn != null" class="text-caption text--secondary mt-1">
                  {{ snapshot.isPluggedIn ? $t('vehicle.telematics-plugged-in') : $t('vehicle.telematics-unplugged') }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Charge limit -->
          <v-col cols="12" sm="6" md="3" v-if="snapshot.chargeLimitPercent != null">
            <v-card outlined class="fill-height">
              <v-card-text class="text-center pa-4">
                <v-icon color="grey" class="mb-2">mdi-speedometer</v-icon>
                <div class="text-caption text--secondary mb-2">{{ $t('vehicle.telematics-charge-limit') }}</div>
                <div class="text-h5">{{ Math.round(snapshot.chargeLimitPercent) }}%</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Charge details (if any charge metric is present) -->
        <template v-if="hasChargeDetails">
          <headline bg class="mt-6">{{ $t('vehicle.telematics-charge') }}</headline>
          <div class="mx-3 mt-3">
            <v-row dense>
              <v-col cols="6" sm="4" md="2" v-if="snapshot.chargeWattage != null">
                <metric-card icon="mdi-flash" :label="$t('vehicle.telematics-wattage')" :value="formatNumber(snapshot.chargeWattage)" unit="W" />
              </v-col>
              <v-col cols="6" sm="4" md="2" v-if="snapshot.chargeVoltage != null">
                <metric-card icon="mdi-sine-wave" :label="$t('vehicle.telematics-voltage')" :value="formatNumber(snapshot.chargeVoltage)" unit="V" />
              </v-col>
              <v-col cols="6" sm="4" md="2" v-if="snapshot.chargeAmperage != null">
                <metric-card icon="mdi-current-ac" :label="$t('vehicle.telematics-amperage')" :value="formatNumber(snapshot.chargeAmperage)" unit="A" />
              </v-col>
              <v-col cols="6" sm="4" md="2" v-if="snapshot.chargeEnergyAddedKwh != null">
                <metric-card icon="mdi-battery-plus" :label="$t('vehicle.telematics-energy-added')" :value="formatNumber(snapshot.chargeEnergyAddedKwh, 1)" unit="kWh" />
              </v-col>
              <v-col cols="6" sm="4" md="2" v-if="snapshot.chargeTimeToCompleteMin != null && snapshot.chargeTimeToCompleteMin > 0">
                <metric-card icon="mdi-timer-outline" :label="$t('vehicle.telematics-time-remaining')" :value="formatDuration(snapshot.chargeTimeToCompleteMin)" />
              </v-col>
              <v-col cols="6" sm="4" md="2" v-if="snapshot.batteryCapacityKwh != null">
                <metric-card icon="mdi-battery-high" :label="$t('vehicle.telematics-battery-capacity')" :value="formatNumber(snapshot.batteryCapacityKwh, 1)" unit="kWh" />
              </v-col>
            </v-row>
          </div>
        </template>

        <!-- Vehicle info -->
        <template v-if="snapshot.vin || snapshot.firmwareVersion || snapshot.vehicleMake">
          <headline bg class="mt-6">{{ $t('vehicle.telematics-vehicle-info') }}</headline>
          <div class="mx-3 mt-3">
            <v-row dense>
              <v-col cols="6" sm="4" md="3" v-if="snapshot.vehicleMake">
                <metric-card icon="mdi-car" :label="$t('vehicle.telematics-vehicle-make-model')" :value="vehicleMakeModelLabel" />
              </v-col>
              <v-col cols="6" sm="4" md="3" v-if="snapshot.vin">
                <metric-card icon="mdi-identifier" label="VIN" :value="snapshot.vin" />
              </v-col>
              <v-col cols="6" sm="4" md="3" v-if="snapshot.firmwareVersion">
                <metric-card icon="mdi-cellphone-arrow-down" :label="$t('vehicle.telematics-firmware')" :value="snapshot.firmwareVersion" />
              </v-col>
            </v-row>
          </div>
        </template>

      </template>
    </template>

    <!-- Not connected → the door is the consent page: associate a customer if needed, then email the link.
         No provider selector, no direct connect: the customer consents, and the connection is created then
         (VIN at grant / OAuth at callback). -->
    <template v-else-if="connection && !connection.connected">
      <!-- Success: the consent page has just been sent -->
      <v-card v-if="consentSent" outlined class="cv-panel mx-auto text-center pa-8">
        <v-avatar color="success" size="64" class="mb-4"><v-icon dark size="34">mdi-email-check-outline</v-icon></v-avatar>
        <div class="text-h6 mb-1">{{ $t('vehicle.consent-link-sent') }}</div>
        <div class="text--secondary">{{ $t('vehicle.telematics-consent-sent-hint') }}</div>
      </v-card>

      <template v-else>
        <!-- Not set up yet → empty-state with the init wizard CTA -->
        <v-card v-if="!connectionReady" outlined class="cv-panel mx-auto text-center pa-8">
          <v-avatar color="grey lighten-3" size="72" class="mb-4"><v-icon color="grey" size="40">mdi-car-connected</v-icon></v-avatar>
          <div class="text-h6 mb-2">{{ $t('vehicle.telematics-not-configured') }}</div>
          <div class="text--secondary mb-6" style="max-width: 420px; margin-inline: auto">{{ $t('vehicle.telematics-consent-intro') }}</div>
          <v-btn v-if="vehicleOrganizationId" color="primary" small depressed rounded @click="showInitWizard = true">
            <v-icon left small>mdi-cog-play-outline</v-icon>
            {{ $t('vehicle.telematics-initialize') }}
          </v-btn>
          <v-alert v-else type="warning" text dense class="mx-auto mb-0" style="max-width: 420px">
            {{ $t('vehicle.telematics-needs-org') }}
          </v-alert>
        </v-card>

        <!-- Initialised → status card + recap + actions -->
        <v-card v-else outlined class="cv-panel mx-auto">
          <!-- header: status + overflow menu (terminate lives here, apart) -->
          <div class="cv-panel__head d-flex align-center pa-4">
            <v-avatar color="orange lighten-5" size="42" class="mr-3"><v-icon color="orange darken-2">mdi-clock-outline</v-icon></v-avatar>
            <div>
              <div class="subtitle-1 font-weight-medium">{{ $t('vehicle.telematics-status-initialised') }}</div>
              <div class="caption text--secondary">{{ $t('vehicle.telematics-status-waiting') }}</div>
            </div>
            <v-spacer />
            <v-chip small color="orange lighten-5" text-color="orange darken-3" class="mr-1">{{ $t('vehicle.telematics-status-pending') }}</v-chip>
            <v-menu offset-y left>
              <template #activator="{ on, attrs }">
                <v-btn icon small v-on="on" v-bind="attrs"><v-icon>mdi-dots-vertical</v-icon></v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="confirmTerminate">
                  <v-list-item-icon class="mr-2"><v-icon small color="error">mdi-link-off</v-icon></v-list-item-icon>
                  <v-list-item-title class="error--text">{{ $t('vehicle.telematics-terminate') }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <v-divider />

          <!-- recap: aligned definition list -->
          <div class="pa-5">
            <!-- customer must be picked (no customer yet) -->
            <div v-if="!vehicle.customer" class="mb-4">
              <div class="caption text--secondary mb-1">{{ $t('vehicle.telematics-customer-step') }}</div>
              <customer-select
                  v-model="wizardCustomerId"
                  :organization-id="vehicleOrganizationId"
                  :label="$t('vehicle.telematics-choose-customer')"
                  dense clearable selection-one-line hide-details
              />
            </div>

            <dl class="cv-defs">
              <template v-if="vehicle.customer">
                <dt>{{ $t('vehicle.telematics-recap-client') }}</dt>
                <dd>{{ customerFullname }}</dd>
              </template>
              <dt>{{ $t('vehicle.telematics-recap-usage') }}</dt>
              <dd>{{ connectionModeLabel }}</dd>
              <dt>{{ $t('vehicle.telematics-recap-type') }}</dt>
              <dd>{{ connectionTypeLabel }}</dd>
              <template v-if="effectiveMode === 'meter'">
                <dt>{{ $t('vehicle.telematics-recap-location') }}</dt>
                <dd><v-icon small color="success">mdi-check</v-icon> {{ $t('vehicle.telematics-location-set') }}</dd>
              </template>
              <!-- Traceability: prove the consent email went out, and when. -->
              <template v-if="pendingConsent">
                <dt>{{ $t('vehicle.telematics-recap-consent-sent') }}</dt>
                <dd v-if="pendingConsent.notifiedAt">
                  <v-icon small color="success">mdi-email-check-outline</v-icon>
                  {{ formatDate(pendingConsent.notifiedAt, $t('format_datetime')) }}
                </dd>
                <dd v-else class="text--secondary">
                  <v-icon small color="grey">mdi-email-off-outline</v-icon> {{ $t('vehicle.telematics-recap-consent-not-sent') }}
                </dd>
              </template>
            </dl>
          </div>

          <v-divider />

          <!-- actions — one entry point that opens the 3-channel dialog (email / copy link / open now). -->
          <div class="pa-4 d-flex align-center flex-wrap" style="gap: 8px">
            <v-btn color="primary" small depressed rounded :disabled="!canSend" @click="openConsentChannels()">
              <v-icon left small>mdi-send-outline</v-icon>
              {{ $t('vehicle.telematics-transmit-consent') }}
            </v-btn>
            <v-btn text small color="primary" rounded @click="showInitWizard = true">
              <v-icon left small>mdi-pencil-outline</v-icon>
              {{ $t('vehicle.telematics-change-connection') }}
            </v-btn>
          </div>
        </v-card>
      </template>
    </template>

    <vehicle-connection-wizard-dialog
        v-model="showInitWizard"
        :vehicle="vehicle"
        :has-meter-location="!!(connection && connection.meter && connection.meter.locationId)"
        :current-mode="effectiveMode"
        :current-type="effectiveType"
        @initialised="onInitialised"
    />

    <!-- Consent transmission channels. Only "open now" carries a returnUrl (back to this portal page); "email"
         and "copy" leave it empty, so the driver's flow ends on the clean confirmation page with no login.
         Shared by the primary send and the multi-connection add (channelProvider set = add that OAuth provider). -->
    <v-dialog v-model="channelDialog" max-width="520">
      <v-card>
        <v-card-title class="text-h6">
          {{ channelProvider
            ? $t('vehicle.telematics-channel-title-provider', { provider: channelProvider.label })
            : $t('vehicle.telematics-channel-title') }}
        </v-card-title>
        <v-card-text>
          <p class="text--secondary mb-4">{{ $t('vehicle.telematics-channel-intro') }}</p>
          <div class="d-flex flex-column" style="gap: 10px">
            <v-btn block color="primary" depressed :loading="busyAction === 'channel-email'" :disabled="isBusy && busyAction !== 'channel-email'" @click="runConsentChannel('email')">
              <v-icon left small>mdi-email-fast-outline</v-icon>
              {{ $t('vehicle.telematics-channel-email') }}
            </v-btn>
            <v-btn block outlined color="primary" :loading="busyAction === 'channel-copy'" :disabled="isBusy && busyAction !== 'channel-copy'" @click="runConsentChannel('copy')">
              <v-icon left small>mdi-content-copy</v-icon>
              {{ $t('vehicle.telematics-channel-copy') }}
            </v-btn>
            <v-btn block text color="primary" :loading="busyAction === 'channel-open'" :disabled="isBusy && busyAction !== 'channel-open'" @click="runConsentChannel('open')">
              <v-icon left small>mdi-open-in-app</v-icon>
              {{ $t('vehicle.telematics-channel-open') }}
            </v-btn>
          </div>

          <!-- The generated link (email/copy) — shown read-only + copyable so the operator can share or test it. -->
          <v-text-field
              v-if="channelLink"
              :value="channelLink"
              :label="$t('vehicle.telematics-channel-link-label')"
              :hint="$t('vehicle.telematics-channel-link-hint')"
              persistent-hint
              readonly
              dense
              outlined
              class="mt-5"
              append-icon="mdi-content-copy"
              @click:append="copyChannelLink"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="channelDialog = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import VehicleRepository from "@repository/VehicleRepository"
import MeterDeviceRepository from "@repository/MeterDeviceRepository"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import 'dayjs/locale/fr'
import 'dayjs/locale/nl'
dayjs.extend(relativeTime)

import { VCard, VCardText, VIcon } from 'vuetify/lib'
import CustomerSelect from "@blocks/Select/CustomerSelect.vue"
import VehicleConnectionWizardDialog from "@pages/Vehicle/modals/VehicleConnectionWizardDialog.vue"

const MetricCard = {
  name: 'MetricCard',
  props: {
    icon: String,
    label: String,
    value: [String, Number],
    unit: String,
  },
  render(h) {
    return h(VCard, { props: { outlined: true }, class: 'fill-height' }, [
      h(VCardText, { class: 'pa-3' }, [
        h('div', { class: 'd-flex align-center mb-1' }, [
          h(VIcon, { props: { xSmall: true }, class: 'mr-1' }, this.icon),
          h('span', { class: 'text-caption text--secondary', style: 'line-height: 1.2' }, this.label),
        ]),
        h('div', { class: 'text-body-1 font-weight-medium' }, [
          String(this.value != null ? this.value : ''),
          this.unit ? h('span', { class: 'text-caption text--secondary ml-1' }, this.unit) : null,
        ]),
      ]),
    ])
  },
}

export default {
  components: { MetricCard, CustomerSelect, VehicleConnectionWizardDialog },

  data() {
    return {
      loadingConnection: true,
      connection: null,
      selectedConnectionIndex: 0,
      refreshing: false,
      registering: false,
      wizardCustomerId: null, // when the vehicle has no customer yet, the one to associate before sending
      adding: false,
      consentSent: false,
      showInitWizard: false,
      // Consent-channel dialog (email / copy link / open now)
      channelDialog: false,
      channelProvider: null, // {value,label,requiresDriverLink} for a multi-connection add; null = primary
      channelLink: null,     // the generated consent link (shown/copyable for the email & copy channels)
      busyAction: null,      // key of the ONE in-flight action; its button spins, the others disable
    }
  },

  computed: {
    // True while any action is running — used to disable the OTHER buttons so only the clicked one spins.
    isBusy() {
      return this.busyAction !== null
    },
    vehicle() {
      return this.$parent.vehicle
    },

    vehicleOrganizationId() {
      return this.vehicle?.organization?.id || null
    },

    // Le fournisseur dont les recharges font foi : choisi sur le compteur, sinon celui du consentement actif.
    chargeProvider() {
      const meter = this.connection && this.connection.meter
      return meter ? (meter.primaryProvider || meter.defaultProvider || null) : null
    },
    canChooseChargeProvider() {
      return this.currentUser.permissions.includes('OPERATOR_WRITE')
    },
    // All active connections (a test vehicle carries several; a prod vehicle exactly one).
    connections() {
      return this.connection?.connections || []
    },

    // The connection whose last known state is shown (driven by the source switcher).
    activeConnection() {
      return this.connections[this.selectedConnectionIndex] || this.connections[0] || {}
    },

    // Inline SVG sparkline of the SoH history (last year). Null until there are at least two points to draw.
    sohSpark() {
      const history = (this.activeConnection.soh || {}).history
      if (!Array.isArray(history) || history.length < 2) return null
      const vals = history.map(p => p.sohPercent)
      const min = Math.min(...vals)
      const max = Math.max(...vals)
      const range = max - min || 1
      const w = 160
      const h = 40
      const pad = 4
      const pts = history.map((p, i) => {
        const x = pad + (i / (history.length - 1)) * (w - 2 * pad)
        const y = pad + (1 - (p.sohPercent - min) / range) * (h - 2 * pad)
        return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 }
      })
      return {
        w,
        h,
        points: pts.map(p => `${p.x},${p.y}`).join(' '),
        last: pts[pts.length - 1],
      }
    },

    // Provider-side onboarding status (Volteras async). Null when fully connected or not applicable → no chip.
    providerStatusChip() {
      const s = this.activeConnection && this.activeConnection.providerStatus
      if (!s || s === 'CONNECTED') return null
      const colors = {
        PENDING: 'orange darken-1',
        PROCESSING: 'orange darken-1',
        CONNECTION_REQUIRED: 'deep-orange',
        INCOMPATIBLE: 'red darken-1',
        ERROR: 'red darken-1',
      }
      const key = 'vehicle.telematics-provider-status-' + s
      return { color: colors[s] || 'grey', label: this.$te(key) ? this.$t(key) : s }
    },

    // Connection HEALTH (Lot 3) → the "action required" banner for the selected connection. Each mode has its own
    // message; OEM_UPSTREAM is NOT user-actionable (retry/wait), so it stays a warning without a call to reconnect.
    healthBanner() {
      const h = this.activeConnection && this.activeConnection.health
      if (!h || h === 'OK') return null
      const map = {
        NEEDS_OEM_REAUTH: { type: 'error', icon: 'mdi-account-alert', key: 'reauth', action: true },
        NEEDS_RECONNECT: { type: 'error', icon: 'mdi-lan-disconnect', key: 'reconnect', action: true },
        OEM_UPSTREAM: { type: 'warning', icon: 'mdi-cloud-alert', key: 'oem-upstream', action: false },
        STALE: { type: 'info', icon: 'mdi-progress-clock', key: 'stale', action: false },
      }
      const m = map[h]
      if (!m) return null
      return {
        type: m.type,
        icon: m.icon,
        title: this.$t('vehicle.telematics-health.' + m.key + '-title'),
        message: this.$t('vehicle.telematics-health.' + m.key + '-message'),
        since: this.activeConnection.healthSince,
        // Only re-auth / reconnect are user-actionable (both resolved by re-running SmartCar Connect); OEM_UPSTREAM
        // and STALE are not — no button, so we never send the owner on a pointless reconnect.
        action: m.action && this.activeConnection.usesOAuth,
      }
    },

    // Event-sourced last known state of the selected source — no live call, just what we already hold.
    snapshot() {
      return this.activeConnection.snapshot || null
    },

    activeVin() {
      return this.activeConnection.externalVehicleId || this.snapshot?.vin || null
    },

    customerFullname() {
      const c = this.vehicle?.customer
      return c ? [c.firstname, c.lastname].filter(Boolean).join(' ') : ''
    },

    // Awaiting-consent config, if any. Before consent nothing is created (no licence), so the recap reads
    // from the PENDING consent; after consent it reads from the licence.
    pendingConsent() {
      return this.connection?.pendingConsent || null
    },
    grantedConsent() {
      return this.connection?.grantedConsent || null
    },

    // The vehicle is initialised once a connection is in flight: a PENDING consent (awaiting the customer)
    // or an active connection licence (already granted).
    connectionReady() {
      return !!this.pendingConsent || !!this.connection?.hasLicence
    },

    // Effective mode/type = the licence's when granted, else the pending consent's.
    effectiveMode() {
      return this.connection?.connectionMode || this.pendingConsent?.mode || null
    },

    effectiveType() {
      return this.connection?.connectionType || this.pendingConsent?.type || null
    },

    connectionModeLabel() {
      const m = this.effectiveMode
      if (m === 'meter') return this.$t('vehicle.conn-wizard.mode-meter')
      if (m === 'telemetry') return this.$t('vehicle.conn-wizard.mode-telemetry')
      return '—'
    },

    // Le type de la connexion AFFICHÉE, et non celui de la licence : un véhicule peut porter une
    // licence « compte constructeur » et, à côté, une connexion Link by Car déclarée par VIN — le
    // bandeau annonçait alors « compte constructeur » en face de Link by Car. On retombe sur la
    // licence quand la connexion ne dit rien (anciennes réponses de l'API).
    activeConnectionTypeLabel() {
      const t = this.activeConnection.connectionType || this.effectiveType
      if (t === 'vin') return this.$t('vehicle.wizard.type-vin')
      if (t === 'oauth') return this.$t('vehicle.wizard.type-oauth')
      return null
    },

    connectionTypeLabel() {
      const t = this.effectiveType
      if (t === 'vin') return this.$t('vehicle.wizard.type-vin')
      if (t === 'oauth') return this.$t('vehicle.wizard.type-oauth')
      return '—'
    },

    // Target mode/type of a pending consent — used for the "change awaiting consent" banner shown while the
    // vehicle is still connected on its current (soon-to-be-replaced) licence.
    pendingModeLabel() {
      const m = this.pendingConsent?.mode
      if (m === 'meter') return this.$t('vehicle.conn-wizard.mode-meter')
      if (m === 'telemetry') return this.$t('vehicle.conn-wizard.mode-telemetry')
      return '—'
    },

    pendingTypeLabel() {
      const t = this.pendingConsent?.type
      if (t === 'vin') return this.$t('vehicle.wizard.type-vin')
      if (t === 'oauth') return this.$t('vehicle.wizard.type-oauth')
      return '—'
    },

    canSend() {
      return this.connectionReady && (!!this.vehicle?.customer || !!this.wizardCustomerId)
    },

    batteryColor() {
      if (!this.snapshot || this.snapshot.batteryLevelPercent == null) return 'grey'
      if (this.snapshot.batteryLevelPercent > 50) return 'success'
      if (this.snapshot.batteryLevelPercent > 20) return 'orange'
      return 'error'
    },

    chargeStateColor() {
      if (!this.snapshot) return 'grey'
      if (this.snapshot.chargingStatus === 'CHARGING' || this.snapshot.isCharging === true) return 'success'
      if (this.snapshot.chargingStatus === 'FULLY_CHARGED') return 'primary'
      return 'grey'
    },

    chargeStateLabel() {
      if (!this.snapshot) return '-'
      if (this.snapshot.chargingStatus) {
        const key = 'vehicle.telematics-charge-state-' + this.snapshot.chargingStatus.toLowerCase()
        const translated = this.$t(key)
        return translated !== key ? translated : this.snapshot.chargingStatus
      }
      if (this.snapshot.isCharging != null) {
        return this.snapshot.isCharging
            ? this.$t('vehicle.telematics-charge-state-charging')
            : this.$t('vehicle.telematics-charge-state-not_charging')
      }
      return '-'
    },

    vehicleMakeModelLabel() {
      if (!this.snapshot) return ''
      const parts = [this.snapshot.vehicleMake, this.snapshot.vehicleModel].filter(Boolean)
      if (this.snapshot.vehicleYear) parts.push('(' + this.snapshot.vehicleYear + ')')
      return parts.join(' ')
    },

    hasChargeDetails() {
      if (!this.snapshot) return false
      return this.snapshot.chargeWattage != null
          || this.snapshot.chargeVoltage != null
          || this.snapshot.chargeAmperage != null
          || this.snapshot.chargeEnergyAddedKwh != null
          || (this.snapshot.chargeTimeToCompleteMin != null && this.snapshot.chargeTimeToCompleteMin > 0)
          || this.snapshot.batteryCapacityKwh != null
    },
  },

  watch: {
    vehicle: {
      immediate: true,
      handler(val) {
        if (val) this.checkConnection()
      },
    },
  },

  methods: {
    onInitialised() {
      this.consentSent = true
      this.checkConnection()
    },

    timeAgo(date) {
      return dayjs(date).locale(this.$i18n.locale).fromNow()
    },

    getProviderIcon(provider) {
      const map = {
        'SMARTCAR': 'mdi-car-connected',
        'GEOTAB': 'mdi-router-wireless',
      }
      return map[provider] || 'mdi-cloud'
    },

    formatNumber(val, decimals = 0) {
      if (val == null) return '-'
      return val.toLocaleString(undefined, { maximumFractionDigits: decimals, minimumFractionDigits: 0 })
    },

    // SoH chip colour = how much we trust the measured capacity (borne/energy > range > power > none).
    sohColor(confidence) {
      return { high: 'green', medium: 'teal', low: 'orange', none: 'grey' }[confidence] || 'grey'
    },

    formatDuration(minutes) {
      if (minutes == null) return '-'
      if (minutes < 60) return minutes + ' min'
      const h = Math.floor(minutes / 60)
      const m = minutes % 60
      return m > 0 ? h + 'h' + String(m).padStart(2, '0') : h + 'h'
    },

    chooseChargeProvider(provider) {
      this.busyAction = 'charge-provider'
      MeterDeviceRepository.choosePrimaryChargeProvider(this.connection.meter.id, provider).then(() => {
        this.checkConnection()
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.busyAction = null
      })
    },

    checkConnection() {
      this.loadingConnection = true
      VehicleRepository.telematics(this.vehicle.id).then(response => {
        this.connection = response
        // Keep the selected source valid if the connection set shrank (e.g. after a terminate).
        if (this.selectedConnectionIndex >= (response.connections?.length || 0)) {
          this.selectedConnectionIndex = 0
        }
        this.loadingConnection = false
      }).catch(() => {
        this.connection = { connected: false }
        this.loadingConnection = false
      })
    },

    // Force a live poll of the SELECTED source, then reload the status so its event-sourced state refreshes.
    refreshSignals() {
      this.refreshing = true
      VehicleRepository.telematicsRefresh(this.vehicle.id, this.activeConnection.provider).then(res => {
        this.refreshing = false
        this.checkConnection()
        // stored === false → the provider hasn't produced a newer measurement since the last reading (frozen
        // re-fetch, deduped): tell the operator instead of leaving the click silently doing nothing.
        if (res && res.stored === false) {
          const when = res.sourceObservedAt || res.collectedAt
          this.$dialog.notify.info(this.$t('vehicle.telematics-refresh-uptodate', { time: this.formatDate(when, this.$t('format_datetime')) }))
        } else {
          this.$dialog.notify.success(this.$t('vehicle.telematics-refresh-stored'))
        }
      }).catch(err => {
        this.refreshing = false
        this.notifyError(err)
      })
    },

    // Reconnect a lost/degraded connection: re-run the SAME OAuth flow (SmartCar Connect) via a re-issued consent
    // with the current config. At grant the backend replaces the dead connection with a fresh healthy one — same
    // vehicle, consent trace kept, no duplicate (the connect handler dedups) — and clears "action required".
    reconnect() {
      this.busyAction = 'reconnect'
      const consent = {
        mode: (this.connection && this.connection.connectionMode) || 'telemetry',
        type: 'oauth',
        provider: this.activeConnection.provider,
        notify: false,
        allowMulti: false,
        returnUrl: this.$route.fullPath,
      }
      VehicleRepository.requestTelematicsConsent(this.vehicle.id, consent)
        .then(res => { if (res && res.link) window.location.href = res.link })
        .catch(err => {
          this.busyAction = null
          this.notifyError(err)
        })
    },

    // Manual notification (Lot 5): email the DRIVER the reconnect link (notify:true) instead of reconnecting here.
    sendReconnectLink() {
      this.busyAction = 'reconnect-email'
      const consent = {
        mode: (this.connection && this.connection.connectionMode) || 'telemetry',
        type: 'oauth',
        provider: this.activeConnection.provider,
        notify: true,
        allowMulti: false,
      }
      VehicleRepository.requestTelematicsConsent(this.vehicle.id, consent)
        .then(() => {
          this.busyAction = null // stop the spinner BEFORE notifying — notify() returns a promise that
          this.$dialog.notify.info(this.$t('vehicle.telematics-health.reconnect-email-sent')) // resolves on toast dismiss
        })
        .catch(err => {
          this.busyAction = null
          this.notifyError(err)
        })
    },

    registerVehicle() {
      this.registering = true
      VehicleRepository.registerTelematics(this.vehicle.id, this.activeConnection.provider).then(() => {
        this.registering = false
        this.$dialog.notify.success(this.$t('vehicle.telematics-declared'))
        this.checkConnection()
      }).catch(err => {
        this.registering = false
        this.notifyError(err)
      })
    },

    // Multi-connection provider pick: OAuth (SmartCar/Enode) needs the driver's link → open the 3-channel
    // consent dialog (keeping the vehicle's other connections); VIN (Volteras/LBC) is declared directly.
    // Gated server-side (ZEborne operator + OPERATOR_WRITE + flag).
    pickMultiProvider(p) {
      if (p.requiresDriverLink) {
        this.openConsentChannels(p)
        return
      }
      if (this.adding) return
      this.adding = true
      VehicleRepository.connectProviderDirect(this.vehicle.id, p.value).then(() => {
        this.adding = false
        this.$dialog.notify.success(this.$t('vehicle.telematics-connection-added'))
        this.checkConnection()
      }).catch(err => {
        this.adding = false
        this.notifyError(err)
      })
    },

    // Open the consent-channel dialog. provider=null → primary connection (uses the vehicle's effective
    // mode/type); provider set → multi-connection add for that OAuth provider.
    openConsentChannels(provider = null) {
      if (provider === null && !this.canSend) return
      this.channelProvider = provider
      this.channelLink = null
      this.busyAction = null
      this.channelDialog = true
    },

    // Request the consent for the chosen channel. Only 'open' carries a returnUrl (back to this portal page);
    // 'email' and 'copy' leave it empty, so the driver's flow ends on the clean confirmation page (no login).
    // notify drives the email: only 'email' sends one — 'copy'/'open' must not email behind the operator's back.
    runConsentChannel(kind) {
      this.busyAction = 'channel-' + kind

      let chain = Promise.resolve()
      // Primary connection on a vehicle with no customer yet: associate the picked customer first.
      if (this.channelProvider === null && !this.vehicle.customer && this.wizardCustomerId) {
        chain = chain
            .then(() => VehicleRepository.assignCustomer(this.vehicle.id, { customerId: this.wizardCustomerId }))
            .then(() => { if (typeof this.$parent.loadData === 'function') this.$parent.loadData() })
      }

      const consent = this.channelProvider
          ? { mode: 'telemetry', type: 'oauth', provider: this.channelProvider.value, allowMulti: true }
          : { mode: this.effectiveMode, type: this.effectiveType }
      if (this.channelProvider === null && this.pendingConsent?.locationId) consent.locationId = this.pendingConsent.locationId
      consent.notify = kind === 'email'
      if (kind === 'open') consent.returnUrl = this.$route.fullPath // relative → returns to this portal page after grant

      chain
          .then(() => VehicleRepository.requestTelematicsConsent(this.vehicle.id, consent))
          .then(res => {
            this.busyAction = null
            if (kind === 'open') {
              if (res && res.link) window.location.href = res.link
              return
            }
            this.channelLink = (res && res.link) || null
            if (kind === 'copy') {
              this.copyChannelLink()
            } else {
              this.$dialog.notify.success(this.$t('vehicle.consent-link-sent'))
            }
            if (this.channelProvider === null) this.consentSent = true
          })
          .catch(err => {
            this.busyAction = null
            this.notifyError(err)
          })
    },

    copyChannelLink() {
      if (!this.channelLink) return
      const done = () => this.$dialog.notify.success(this.$t('vehicle.telematics-channel-copied'))
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.channelLink).then(done).catch(() => {})
      } else {
        done()
      }
    },

    // Résilier the SELECTED source. On a multi-connection vehicle that means dropping only this provider's
    // connection (the others + the shared licence stay). On a single-connection vehicle it is the full
    // teardown: licence(s) + consent + disconnect, back to the not-initialised state.
    confirmTerminate() {
      const multi = this.connections.length > 1
      const provider = this.activeConnection.provider
      const text = multi
          ? this.$t('vehicle.telematics-confirm-disconnect-source', { provider })
          : this.$t('vehicle.telematics-confirm-terminate')

      this.$dialog.confirm({ text }).then(result => {
        if (!result) return
        this.showLoading(true)
        const request = multi
            ? VehicleRepository.disconnectTelematics(this.vehicle.id, provider)
            : VehicleRepository.terminateTelematicsConnection(this.vehicle.id)
        request.then(() => {
          this.showLoading(false)
          this.consentSent = false
          this.selectedConnectionIndex = 0
          this.checkConnection()
        }).catch(err => {
          this.showLoading(false)
          this.notifyError(err)
        })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.cv-panel {
  max-width: 560px;
  border-radius: 14px;
  overflow: hidden;
}
.cv-panel__head {
  background: #fafbfc;
}
.cv-defs {
  display: grid;
  grid-template-columns: 150px 1fr;
  row-gap: 14px;
  column-gap: 20px;
  margin: 0;
}
.cv-defs dt {
  color: #90a4ae;
  font-size: 13px;
  align-self: center;
}
.cv-defs dd {
  margin: 0;
  font-weight: 600;
  color: #263238;
}
.cv-sources {
  flex-wrap: wrap;
}
.cv-identity {
  display: flex;
  flex-wrap: wrap;
  gap: 22px;
  padding: 12px 16px;
  background: #f7f8fa;
  border-radius: 12px;
}
.cv-identity__item {
  display: flex;
  flex-direction: column;
}
.cv-identity__k {
  font-size: 11px;
  color: #90a4ae;
  text-transform: uppercase;
  letter-spacing: .03em;
}
.cv-identity__v {
  font-weight: 600;
  color: #263238;
  font-size: 14px;
}
</style>
